import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Cookie } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useLocale } from "@/i18n/LocaleProvider";
import { acceptConsent, declineConsent, getConsent } from "@/lib/tracking";

/**
 * GDPR cookie-consent banner. Only shown when tracking is enabled, a consent
 * banner is requested, and the visitor has not yet made a choice. Choices are
 * persisted in localStorage by the tracking engine.
 */
const ConsentBanner = ({ show }: { show: boolean }) => {
  const { t } = useTranslation();
  const { localize } = useLocale();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (show && getConsent() === null) {
      // Small delay so it animates in after first paint.
      const id = window.setTimeout(() => setVisible(true), 600);
      return () => window.clearTimeout(id);
    }
    setVisible(false);
  }, [show]);

  if (!visible) return null;

  const close = () => setVisible(false);

  return (
    <div role="region" aria-label={t("consent.privacyLink")} className="fixed inset-x-0 bottom-0 z-[95] px-4 pb-4 animate-in slide-in-from-bottom-6 duration-500">
      <div className="mx-auto max-w-2xl rounded-xl border border-border bg-card/95 backdrop-blur-xl px-4 py-3.5 md:px-5 md:py-4 shadow-2xl">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-5">
          <div className="flex items-start gap-3 min-w-0">
            <div className="hidden sm:flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/10">
              <Cookie size={16} className="text-accent" />
            </div>
            <p className="text-[13px] text-muted-foreground leading-relaxed">
              {t("consent.body")}{" "}
              <Link to={localize("/privacy-policy")} className="text-accent underline underline-offset-2 hover:no-underline">
                {t("consent.privacyLink")}
              </Link>
              .
            </p>
          </div>
          <div className="flex shrink-0 gap-2.5 md:gap-3">
            <Button
              size="sm"
              className="btn-clip flex-1 md:flex-none"
              onClick={() => {
                acceptConsent();
                close();
              }}
            >
              {t("consent.accept")}
            </Button>
            <Button
              size="sm"
              variant="outline"
              className="btn-clip flex-1 md:flex-none"
              onClick={() => {
                declineConsent();
                close();
              }}
            >
              {t("consent.decline")}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ConsentBanner;
